import type { ReactNode } from "react";
import { AlertTriangleIcon } from "lucide-react";

import { errorText } from "@/lib/format";

// PageHeader forces every panel to declare a scope alongside its title: the
// `scope` prop is required, so a new panel cannot ship without saying whose
// data it shows (see components/scope.tsx for why that matters).
export function PageHeader({
  title,
  titleId,
  scope,
  actions,
}: {
  title: string;
  titleId?: string;
  scope: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="nb-page-header flex flex-wrap items-start justify-between gap-3">
      <div className="space-y-1">
        <h1 id={titleId} className="nb-page-title">{title}</h1>
        {scope}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ErrorNote renders a failed call. It renders nothing for a null error so
// callers can drop it in unconditionally.
export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      data-testid="error-note"
      className="nb-alert nb-alert--danger mb-4 flex items-start gap-2"
    >
      <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
      <span>{errorText(error)}</span>
    </div>
  );
}

export function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <div
      data-testid="empty-note"
      className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground"
    >
      {children}
    </div>
  );
}

export function Loading() {
  return (
    <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
  );
}
